import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

export default function FAQ() {
  const [openItem, setOpenItem] = useState<string | null>('faq-trial');

  const faqData: FAQItem[] = [
    {
      id: 'faq-trial',
      question: 'How does the free trial work?',
      answer: 'Our 14-day free trial gives you full access to all MandaPost features with no credit card required. You can connect up to 5 social media accounts, schedule unlimited posts, and explore all AI-powered tools. After the trial ends, you can choose a plan that fits your needs or continue with our free tier with limited features.',
      category: 'Getting Started'
    },
    {
      id: 'faq-platforms',
      question: 'Which platforms are supported?',
      answer: 'MandaPost currently supports Facebook, Instagram, Twitter/X, LinkedIn, TikTok, Pinterest, and YouTube. We\'re constantly adding new platforms based on user demand. Each platform has full scheduling capabilities, analytics tracking, and platform-specific content optimization through our intelligent tools.',
      category: 'Features'
    },
    {
      id: 'faq-cancel',
      question: 'Can I cancel anytime?',
      answer: 'Yes, absolutely! You can cancel your subscription at any time with no cancellation fees or penalties. If you cancel, you\'ll continue to have access to your paid features until the end of your current billing period. You can also downgrade to our free plan to keep your account and data without losing access.',
      category: 'Billing'
    },
    {
      id: 'faq-security',
      question: 'Is my data secure?',
      answer: 'Security is our top priority. We use 256-bit SSL encryption for all data transfers, store passwords using industry-standard hashing algorithms, and are fully GDPR and CCPA compliant. Your social media credentials are encrypted and stored securely using OAuth 2.0. We never sell or share your data with third parties, and you can export or delete your data at any time.',
      category: 'Security & Privacy'
    },
    {
      id: 'faq-ai',
      question: 'How does AI content work?',
      answer: 'Our AI analyzes your brand voice, audience engagement patterns, and trending topics to generate relevant content suggestions. It can create post captions, hashtag recommendations, and optimal posting times. The AI learns from your preferences over time and adapts to your specific industry and audience. You always have full control to edit, approve, or reject any generated content before publishing.',
      category: 'Features'
    },
    {
      id: 'faq-pricing',
      question: 'What happens if I exceed my plan limits?',
      answer: 'If you reach your plan\'s limits (social accounts, scheduled posts, or team members), we\'ll notify you in advance. You can either upgrade to a higher tier plan or manage your current usage. We never charge overage fees without your explicit approval. You\'ll always have the option to upgrade, and the price difference will be prorated based on your billing cycle.',
      category: 'Billing'
    },
    {
      id: 'faq-team',
      question: 'Can I add team members?',
      answer: 'Yes! All paid plans include team collaboration features. You can add team members with different permission levels: Admin (full access), Editor (create and schedule posts), Viewer (view-only access), or Client (limited dashboard access). Team members can collaborate on content calendars, leave comments, and receive notifications based on their role.',
      category: 'Team Collaboration'
    },
    {
      id: 'faq-analytics',
      question: 'What analytics and reporting are included?',
      answer: 'MandaPost provides comprehensive analytics including engagement rates, follower growth, best posting times, content performance, and competitor benchmarking. You can generate custom reports, export data to CSV/PDF, and set up automated weekly or monthly report deliveries. Pro and Enterprise plans include advanced analytics with historical data up to 2 years and custom metric tracking.',
      category: 'Features'
    },
    {
      id: 'faq-migration',
      question: 'Can I migrate from another tool?',
      answer: 'Yes! We offer free migration assistance for all paid plans. Our team can help you import your content calendar, media library, and team settings from tools like Hootsuite, Buffer, Sprout Social, and Later. The migration process typically takes 1-3 business days depending on the amount of data. Contact our support team to schedule your migration.',
      category: 'Getting Started'
    },
    {
      id: 'faq-support',
      question: 'What kind of customer support do you offer?',
      answer: 'We offer multiple support channels: email support for all users (24-48 hour response time), live chat for paid plans (typically under 4 hours), and priority phone support for Pro and Enterprise customers. We also have an extensive knowledge base, video tutorials, and weekly webinars. Enterprise customers get a dedicated account manager and onboarding specialist.',
      category: 'Support'
    },
    {
      id: 'faq-mobile',
      question: 'Is there a mobile app?',
      answer: 'Yes! MandaPost is available on iOS and Android. The mobile app lets you create and schedule posts, respond to comments, view analytics, and receive push notifications for important events. You can also use the mobile app to capture and upload media directly to your library. The app syncs seamlessly with the web platform in real-time.',
      category: 'Features'
    },
    {
      id: 'faq-content-library',
      question: 'How does the media library work?',
      answer: 'Your media library provides unlimited storage for images, videos, and documents (varies by plan). You can organize files into folders, add tags for easy searching, and access a built-in image editor with filters, cropping, and text overlays. The library integrates with Unsplash and Giphy for free stock content, and supports bulk uploads via drag-and-drop or cloud imports from Google Drive and Dropbox.',
      category: 'Features'
    }
  ];

  const categories = Array.from(new Set(faqData.map(item => item.category)));

  const toggleItem = (id: string) => {
    setOpenItem(openItem === id ? null : id);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-white to-[#e4f4ff]/10">
      <Header />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 rounded-full text-white font-medium mb-6">
              <i className="ri-question-answer-line mr-2"></i>
              Frequently Asked Questions
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold mb-6">
              How Can We Help You?
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Find answers to common questions about MandaPost features, pricing, security, and more.
            </p>
          </div>
        </section>

        {/* Search Bar */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <i className="ri-search-line text-gray-400 text-xl"></i>
              </div>
              <input
                type="text"
                placeholder="Search for answers..."
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#38a3a5] focus:border-transparent transition-all text-base"
              />
            </div>
          </div>
        </section>

        {/* FAQ Categories */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex flex-wrap gap-3 mb-12 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                className="px-4 py-2 bg-white border border-gray-200 rounded-full text-gray-700 hover:border-[#38a3a5] hover:text-[#38a3a5] transition-colors font-medium"
              >
                {category}
              </button>
            ))}
          </div>

          {/* FAQ Accordion */}
          <div className="space-y-4">
            {faqData.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden transition-all hover:shadow-md"
              >
                <button
                  onClick={() => toggleItem(item.id)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-start flex-1 pr-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-question-line text-white text-lg"></i>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-[#38a3a5] mb-1 block">
                        {item.category}
                      </span>
                      <h3 className="text-lg font-bold text-[#22577A]">
                        {item.question}
                      </h3>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <i
                      className={`ri-arrow-${openItem === item.id ? 'up' : 'down'}-s-line text-2xl text-gray-400 transition-transform`}
                    ></i>
                  </div>
                </button>

                {openItem === item.id && (
                  <div className="px-6 pb-6 pt-2 animate-fade-in">
                    <div className="pl-14">
                      <p className="text-gray-700 leading-relaxed">{item.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Still Have Questions CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          <div className="bg-gradient-to-r from-[#22577A] to-[#38a3a5] rounded-3xl p-8 lg:p-12 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-customer-service-2-line text-3xl"></i>
            </div>
            <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Our support team is here to help! Get in touch and we'll respond within 24 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#22577A] rounded-xl font-semibold text-lg hover:shadow-xl hover:scale-105 transition-all"
              >
                <i className="ri-mail-line mr-2"></i>
                Contact Support
              </a>
              <a
                href="#help"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/20 backdrop-blur-sm border-2 border-white text-white rounded-xl font-semibold text-lg hover:bg-white/30 transition-all"
              >
                <i className="ri-book-open-line mr-2"></i>
                Visit Help Center
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
